import { useState, useMemo, useCallback } from 'react';
import { useHotelSearch } from '../hooks/useHotelSearch';
import HotelCard from './HotelCard';
import HotelFilters from './HotelFilters';
import {
  Building2,
  Users,
  Calendar,
  Loader2,
  Search,
  X,
  Settings,
  Bed,
  MapPin
} from 'lucide-react';
import type {
  HotelSearchParams,
  HotelFilters as HotelFiltersType,
  HotelSortOption,
  StarRating,
  Hotel as ComprehensiveHotel,
  Hotel
} from '../types/hotel';

// Import actual Hotel type from hook for internal use
import type { Hotel as HotelSearchResult } from '../hooks/useHotelSearch';

interface HotelSearchProps {
  onSelectHotel?: (hotel: Hotel) => void;
  onAddToItinerary?: (hotel: Hotel) => void;
  className?: string;
  initialCity?: string;
}

interface SearchFormData {
  destination: {
    city: string;
    country?: string;
  };
  checkIn: string;
  checkOut: string;
  rooms: number;
  guests: {
    adults: number;
    children: number;
  };
  starRating: StarRating[];
}

const defaultFilters: HotelFiltersType = {
  priceRange: { min: 0, max: 1000 },
  starRating: [5, 4, 3, 2, 1],
  amenities: [],
  guestRating: { min: 0 },
  distance: { maxFromCenter: 50 },
  propertyType: [],
  freeCancellation: false,
  bookingOptions: {
    payLater: false,
    freeBreakfast: false,
  },
  accessibility: {
    wheelchairAccessible: false,
    accessibleParking: false,
  },
};

export default function HotelSearch({ 
  onSelectHotel, 
  onAddToItinerary,
  className = '',
  initialCity = ''
}: HotelSearchProps) {
  const {
    results,
    loading,
    error,
    search,
    clearResults,
    demoMode,
  } = useHotelSearch();

  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    destination: {
      city: initialCity,
      country: '',
    },
    checkIn: '',
    checkOut: '',
    rooms: 1,
    guests: {
      adults: 2,
      children: 0,
    },
    starRating: [5, 4, 3, 2, 1],
  });

  const [filters, setFilters] = useState<HotelFiltersType>(defaultFilters);
  const [sortBy, setSortBy] = useState<HotelSortOption>('price-asc');

  // Calculate number of nights
  const numberOfNights = useMemo(() => {
    if (!searchForm.checkIn || !searchForm.checkOut) return 0;
    const start = new Date(searchForm.checkIn);
    const end = new Date(searchForm.checkOut);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [searchForm.checkIn, searchForm.checkOut]);

  // Convert form data to search params
  const searchParams: HotelSearchParams | null = useMemo(() => {
    if (!searchForm.destination.city || !searchForm.checkIn || !searchForm.checkOut) {
      return null;
    }

    return {
      destination: searchForm.destination,
      checkIn: searchForm.checkIn,
      checkOut: searchForm.checkOut,
      rooms: searchForm.rooms,
      guests: searchForm.guests,
      filters,
      sortBy,
      maxResults: 20,
    };
  }, [searchForm, filters, sortBy]);

  // Apply filters and sorting to results
  const filteredResults = useMemo(() => {
    let filtered = [...results];

    // Apply price filter
    filtered = filtered.filter(hotel =>
      hotel.pricePerNight >= filters.priceRange.min &&
      hotel.pricePerNight <= filters.priceRange.max
    );

    // Apply sorting
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.pricePerNight - b.pricePerNight);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.pricePerNight - a.pricePerNight);
        break;
      case 'rating-desc':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
    }

    return filtered;
  }, [results, filters, sortBy]);

  // Get unique amenities for filter (not available in current API response)
  const availableAmenities = useMemo(() => {
    return [];
  }, [results]);

  // Transform API response to comprehensive Hotel type
  const transformHotel = (apiHotel: HotelSearchResult): ComprehensiveHotel => {
    // Convert image URLs to proper HotelImage objects
    const hotelImages = apiHotel.images.map((url, index) => ({
      id: `${apiHotel.id}-img-${index}`,
      url,
      caption: `${apiHotel.name} - Image ${index + 1}`,
      category: 'other' as const,
      isPrimary: index === 0,
    }));

    // Convert amenity strings to proper HotelAmenity objects
    const hotelAmenities = apiHotel.amenities.map((name, index) => ({
      id: `${apiHotel.id}-amenity-${index}`,
      name,
      category: 'general' as const,
    }));

    return {
      id: apiHotel.id,
      name: apiHotel.name,
      description: `${apiHotel.name} in ${apiHotel.city}`,
      location: {
        city: apiHotel.city,
        country: '',
      },
      address: {
        street: '',
        city: apiHotel.city,
        country: '',
        formattedAddress: apiHotel.address,
      },
      coordinates: {
        lat: apiHotel.location.lat,
        lng: apiHotel.location.lng,
      },
      rating: {
        overall: apiHotel.rating,
        cleanliness: apiHotel.rating,
        comfort: apiHotel.rating,
        location: apiHotel.rating,
        facilities: apiHotel.rating,
        staff: apiHotel.rating,
        value: apiHotel.rating,
        maxRating: 5,
      },
      starRating: 3, // Default, not available in API
      reviews: {
        totalCount: apiHotel.reviewCount,
        averageRating: apiHotel.rating,
        breakdown: {
          excellent: Math.floor(apiHotel.reviewCount * 0.4),
          veryGood: Math.floor(apiHotel.reviewCount * 0.3),
          average: Math.floor(apiHotel.reviewCount * 0.15),
          poor: Math.floor(apiHotel.reviewCount * 0.1),
          terrible: Math.floor(apiHotel.reviewCount * 0.05),
        },
      },
      images: hotelImages,
      price: {
        perNight: apiHotel.pricePerNight,
        total: apiHotel.totalPrice,
        currency: apiHotel.currency,
        taxes: 0,
        fees: 0,
      },
      rooms: [{
        id: apiHotel.id,
        name: apiHotel.type,
        description: `${apiHotel.type} room at ${apiHotel.name}`,
        maxOccupancy: 2,
        maxAdults: 2,
        maxChildren: 1,
        bedType: 'double',
        bedCount: 1,
        price: {
          perNight: apiHotel.pricePerNight,
          total: apiHotel.totalPrice,
          currency: apiHotel.currency,
          taxes: 0,
          fees: 0,
        },
        available: true,
        availableCount: 1,
        amenities: apiHotel.amenities,
        images: hotelImages,
      }],
      availableRooms: 1,
      amenities: hotelAmenities,
      policies: {
        checkIn: { time: '15:00' },
        checkOut: { time: '11:00' },
        children: { allowed: true },
        pets: { allowed: false },
        cancellation: { freeCancellation: apiHotel.cancellationPolicy.toLowerCase().includes('free') },
        payment: {
          acceptedPaymentMethods: [],
          depositRequired: false,
          prepaymentRequired: false,
        },
      },
      contact: {
        phone: '',
      },
      provider: 'Amadeus',
      lastUpdated: new Date().toISOString(),
    };
  };

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchForm.destination.city || !searchForm.checkIn || !searchForm.checkOut) return;

    clearResults();
    // Convert to the format the hook expects
    await search({
      cityCode: searchForm.destination.city,
      checkInDate: searchForm.checkIn,
      checkOutDate: searchForm.checkOut,
      adults: searchForm.guests.adults,
      roomQuantity: searchForm.rooms,
    });
  }, [searchForm, search, clearResults]);

  const handleClearSearch = useCallback(() => {
    clearResults();
    setSearchForm({
      destination: { city: '', country: '' },
      checkIn: '',
      checkOut: '',
      rooms: 1,
      guests: { adults: 2, children: 0 },
      starRating: [5, 4, 3, 2, 1],
    });
  }, [clearResults]);

  const updateGuests = (type: keyof SearchFormData['guests'], delta: number) => {
    setSearchForm(prev => ({
      ...prev,
      guests: {
        ...prev.guests,
        [type]: Math.max(1, type === 'adults' ? prev.guests.adults + delta : prev.guests.children + delta),
      },
    }));
  };

  const today = new Date().toISOString().split('T')[0];
  const minCheckOut = searchForm.checkIn || today;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Building2 className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Hotel Search</h2>
            <p className="text-sm text-gray-500">
              {filteredResults.length} hotels found
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${
              showFilters ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <Settings className="h-5 w-5" />
          </button>
          <button
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            className="text-gray-400 hover:text-gray-600"
          >
            {isSearchExpanded ? (
              <X className="h-5 w-5" />
            ) : (
              <Search className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Demo Mode Banner */}
      {demoMode && results.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800">
          💡 Showing demo data. Configure Amadeus API keys for real hotel search.
        </div>
      )}

      {/* Search Form */}
      {isSearchExpanded && (
        <form onSubmit={handleSearch} className="p-4 space-y-4">
          {/* Destination */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <MapPin className="h-3 w-3 inline mr-1" />
              Destination
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="City, region, or hotel name"
                className="input-field pl-9"
                value={searchForm.destination.city}
                onChange={(e) => setSearchForm(prev => ({
                  ...prev,
                  destination: { ...prev.destination, city: e.target.value }
                }))}
              />
              <MapPin className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Calendar className="h-3 w-3 inline mr-1" />
                Check-in
              </label>
              <input
                type="date"
                className="input-field"
                value={searchForm.checkIn}
                onChange={(e) => setSearchForm(prev => ({ ...prev, checkIn: e.target.value }))}
                min={today}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Calendar className="h-3 w-3 inline mr-1" />
                Check-out
              </label>
              <input
                type="date"
                className="input-field"
                value={searchForm.checkOut}
                onChange={(e) => setSearchForm(prev => ({ ...prev, checkOut: e.target.value }))}
                min={minCheckOut}
                required
              />
            </div>

            {/* Nights indicator */}
            <div className="flex items-end pb-1">
              {numberOfNights > 0 ? (
                <span className="text-sm text-gray-600 font-medium">
                  {numberOfNights} night{numberOfNights > 1 ? 's' : ''}
                </span>
              ) : (
                <span className="text-sm text-gray-400">Select dates</span>
              )}
            </div>

            {/* Search Button */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                &nbsp;
              </label>
              <button
                type="submit"
                disabled={loading || !searchParams}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Search Hotels
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Rooms and Guests */}
          <div className="flex flex-wrap gap-4">
            {/* Rooms */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Bed className="h-3 w-3 inline mr-1" />
                Rooms
              </label>
              <select
                className="input-field"
                value={searchForm.rooms}
                onChange={(e) => setSearchForm(prev => ({ 
                  ...prev, 
                  rooms: parseInt(e.target.value) 
                }))}
              >
                {[1, 2, 3, 4, 5].map(n => (
                  <option key={n} value={n}>{n} room{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>

            {/* Adults */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Users className="h-3 w-3 inline mr-1" />
                Adults
              </label>
              <select
                className="input-field"
                value={searchForm.guests.adults}
                onChange={(e) => updateGuests('adults', parseInt(e.target.value) - searchForm.guests.adults)}
              >
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            {/* Children */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Children
              </label>
              <select
                className="input-field"
                value={searchForm.guests.children}
                onChange={(e) => updateGuests('children', parseInt(e.target.value) - searchForm.guests.children)}
              >
                {[0, 1, 2, 3, 4].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Search */}
          {results.length > 0 && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear search and start over
            </button>
          )}
        </form>
      )}

      {/* Filters Panel */}
      {showFilters && results.length > 0 && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <HotelFilters
            filters={filters}
            onFiltersChange={setFilters}
            sortBy={sortBy}
            onSortChange={setSortBy}
            availableAmenities={availableAmenities}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border-t border-red-100">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Results */}
      {filteredResults.length > 0 && (
        <div className="border-t border-gray-100">
          {/* Sort Bar */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <span className="text-sm text-gray-600">
              {filteredResults.length} hotels
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as HotelSortOption)}
                className="text-sm border-0 bg-transparent focus:ring-0 text-gray-700"
              >
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="rating-desc">Guest Rating</option>
                <option value="stars-desc">Star Rating</option>
                <option value="reviews-desc">Most Reviewed</option>
              </select>
            </div>
          </div>

          {/* Hotel Cards */}
          <div className="divide-y divide-gray-100">
            {filteredResults.slice(0, 10).map((hotel, index) => (
              <HotelCard
                key={hotel.id || index}
                hotel={transformHotel(hotel)}
                numberOfNights={numberOfNights}
                onSelect={onSelectHotel}
                onAddToItinerary={onAddToItinerary}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {results.length > 0 && filteredResults.length === 0 && !loading && (
        <div className="p-8 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No hotels found</h3>
          <p className="text-sm text-gray-500 mb-4">
            Try adjusting your filters or search criteria
          </p>
          <button
            onClick={() => setFilters(defaultFilters)}
            className="btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Initial State */}
      {results.length === 0 && !loading && (
        <div className="p-8 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Search for hotels</h3>
          <p className="text-sm text-gray-500">
            Enter your destination and dates to find the best hotels
          </p>
        </div>
      )}
    </div>
  );
}
